/**@jsxImportSource @emotion/react */
import { ClassAttributes, HTMLAttributes, ReactNode, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { createPopper, preventOverflow, flip, State, Placement } from '@popperjs/core';
import { ClickAwayListener, TypographyProps } from '@mui/material';
import { BORDER_COLOR, ERROR_BORDER } from '../../../typings';
import { QSTypography } from '../../atoms';

interface DropdownProps {
  trigger: ReactNode;
  position?: 'top' | 'bottom';
  children: ReactNode | ReactNode[];
  open: boolean;
  setOpen: (open: boolean) => void;
  disabled?: boolean;
  hasError?: boolean;
  customWidth?: string;
  headerTitle?: string;
  headerTitleStyles?: TypographyProps;
  containerProps?: ClassAttributes<HTMLDivElement> & HTMLAttributes<HTMLDivElement>;
  activeBorderColor?: string;
  hoverBorderColor?: string;
  dropdownWidthAsPopperMinWidth?: boolean;
  outerContainerCss?: Interpolation<Theme>;
  fullWidth?: boolean;
  triggerContainerCss?: Interpolation<Theme>;
  offsetModifier?: number[];
  placement?: Placement;
}

const DTDropdown = ({
  children,
  trigger,
  open,
  setOpen,
  containerProps,
  position = 'top',
  hasError = false,
  disabled = false,
  headerTitle = '',
  headerTitleStyles,
  customWidth,
  activeBorderColor,
  dropdownWidthAsPopperMinWidth,
  outerContainerCss,
  triggerContainerCss,
  hoverBorderColor = '#2d2d2d',
  fullWidth,
  offsetModifier,
  placement,
}: DropdownProps) => {
  const outerContainerRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = useCallback(() => {
    if (!disabled) setOpen(!open);
  }, [disabled, open, setOpen]);

  useEffect(() => {
    const outerContainer = outerContainerRef.current;
    if (outerContainer) {
      const onEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          setOpen(false);
        }
      };

      document.addEventListener('keydown', onEscape);

      return () => {
        document.removeEventListener('keydown', onEscape);
      };
    }
  }, [setOpen]);

  useLayoutEffect(() => {
    if (dropdownRef.current && outerContainerRef.current && open) {
      createPopper(outerContainerRef.current as Element, dropdownRef.current, {
        strategy: 'fixed',
        placement: placement ? placement : 'bottom',
        modifiers: [
          preventOverflow,
          flip,
          {
            name: 'offset',
            options: {
              offset: offsetModifier ? offsetModifier : [0, 4],
            },
          },
          {
            name: 'sameWidth',
            enabled: true,
            fn: ({ state }: { state: State }) => {
              state.styles.popper.width = customWidth ? customWidth : `${state.rects.reference.width}px`;
              state.styles.popper.minWidth = dropdownWidthAsPopperMinWidth
                ? `${state.rects.reference.width}px`
                : state.styles.popper.minWidth;
            },
            effect({ state }: { state: State }) {
              state.elements.popper.style.width = `${(state.elements.reference as HTMLElement).offsetWidth}px`;
            },
            phase: 'beforeWrite',
            requires: ['computeStyles'],
          },
        ],
      });
    }
  }, [customWidth, open]);
  const borderColor = open ? activeBorderColor ?? BORDER_COLOR : BORDER_COLOR;

  const handleClickAway = useCallback(
    (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      const isClickInsideDropdown = dropdownRef.current?.contains(target) ?? false;
      const isClickInsideTrigger = outerContainerRef.current?.contains(target) ?? false;

      // Check if the target is an input element that might be inside the dropdown
      const isInputElement =
        target instanceof Element &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.closest('input') !== null ||
          target.closest('textarea') !== null);

      // Don't close if clicking inside the dropdown content
      if (isClickInsideDropdown) {
        return;
      }

      // Don't close if clicking on the trigger (it will toggle itself)
      if (isClickInsideTrigger) {
        return;
      }

      // Don't close if clicking on an input element (might be a search input)
      // This is a safeguard in case the input is not properly contained
      if (isInputElement && open) {
        return;
      }

      setOpen(false);
    },
    [open, setOpen]
  );

  return (
    <div
      css={css`
        position: relative;
        display: flex;
        flex-direction: column;
        gap: 8px;
        align-items: flex-start;
        width: 100%;
        max-width: ${!fullWidth ? '300px' : '100%'};
      `}
      {...containerProps}
    >
      {headerTitle ? (
        <QSTypography
          sx={{
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: '600',
            lineHeight: 'normal',
            ...headerTitleStyles?.sx,
          }}
          {...headerTitleStyles}
        >
          {headerTitle}
        </QSTypography>
      ) : null}
      <ClickAwayListener onClickAway={handleClickAway} mouseEvent="onMouseDown" touchEvent="onTouchStart">
        <div
          ref={outerContainerRef}
          css={[
            css`
              width: 100%;
              background: white;
              border: 1px solid ${hasError ? ERROR_BORDER : borderColor};
              border-radius: 6px;
              height: 100%;
              cursor: pointer;
              overflow: hidden;

              &:hover {
                border-color: ${activeBorderColor && open ? activeBorderColor : hoverBorderColor};
              }
            `,
            outerContainerCss,
          ]}
        >
          <div
            css={[
              css`
                width: 100%;
              `,
              triggerContainerCss,
            ]}
            onClick={(e) => {
              if (!disabled) {
                toggleDropdown();
              }
            }}
          >
            {trigger}
          </div>
          {open && (
            <div
              tabIndex={0}
              ref={dropdownRef}
              onClick={(e) => {
                // Prevent clicks inside dropdown from bubbling up
                e.stopPropagation();
              }}
              onMouseDown={(e) => {
                // Prevent mousedown from triggering click-away
                e.stopPropagation();
              }}
              css={css`
                position: fixed;
                z-index: 1000;
                background: white;
                border: 1px solid ${BORDER_COLOR};
                border-radius: 6px;
                ${customWidth ? `width: ${customWidth}; !important` : ''}
              `}
            >
              {children}
            </div>
          )}
        </div>
      </ClickAwayListener>
    </div>
  );
};

export default DTDropdown;
